import React, { useState, useEffect } from "react";
import { db, storage } from "../../firebase/firebase";
import {
  collection,
  doc,
  setDoc,
  addDoc,
  getDocs,
  query,
  orderBy,
  serverTimestamp,
} from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { logActivity } from "../../utils/analyticsActivity";
import { useNavigate } from "react-router-dom";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import AdminNavBar from "../Admin/AdminNavBar";


/* ----------------------------------------------------------
   TAG LISTS
---------------------------------------------------------- */

const PRODUCT_TAGS = [
  "Animals", "Nature", "Kids", "Relaxing", "Cute", "Holiday", "Fantasy",
  "Beginner", "Advanced", "Motivational", "Patterns", "Flowers", "Ocean",
  "Creativity", "Mindfulness",
];

const BLOG_GENRE_TAGS = [
  "Fantasy", "Science Fiction", "Adventure", "Mystery", "Horror", "Romance",
  "Drama", "Comedy", "Supernatural", "Mythology", "Fairy Tales", "Slice of Life",
  "Dark Fantasy", "Post-Apocalyptic", "Space Opera", "Cyberpunk", "Steampunk",
  "Heroic Quest", "Time Travel", "Paranormal", "Thriller", "Wholesome",
  "Dreamscape", "Cosmic", "Tiny Tales", "Flash Fiction", "Micro Stories",
  "Character Moments", "World-Building",
];

/* ----------------------------------------------------------
   IMAGE COMPRESSION
---------------------------------------------------------- */

async function compressImage(file, maxWidth = 1400) {
  const bitmap = await createImageBitmap(file);
  const ratio = bitmap.width > maxWidth ? maxWidth / bitmap.width : 1;

  const canvas = document.createElement("canvas");
  canvas.width = bitmap.width * ratio;
  canvas.height = bitmap.height * ratio;

  const ctx = canvas.getContext("2d");
  ctx.drawImage(bitmap, 0, 0, canvas.width, canvas.height);

  return new Promise((resolve) => {
    canvas.toBlob(
      (blob) => resolve(new File([blob], file.name, { type: blob.type })),
      "image/jpeg",
      0.8
    );
  });
}

/* ----------------------------------------------------------
   MAIN PAGE
---------------------------------------------------------- */

const AdminUpload = () => {
  const navigate = useNavigate();
  const [type, setType] = useState("");
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    async function loadCategories() {
      const q = query(collection(db, "categories"), orderBy("order", "asc"));
      const snap = await getDocs(q);
      setCategories(snap.docs.map((doc) => ({ id: doc.id, ...doc.data() })));
    }
    loadCategories();
  }, []);

  return (
    <div className="upload-home">

      <AdminNavBar />

      <div style={styles.container}>
        <h1 style={styles.title}>Upload Content</h1>

        <div style={styles.row}>
          <label style={styles.label}>Upload Type</label>
          <select
            style={styles.input}
            value={type}
            onChange={(e) => setType(e.target.value)}
          >
            <option value="">Select One</option>
            <option value="product">Product</option>
            <option value="blog">Blog Post</option>
          </select>
        </div>

        {type === "product" && (
          <ProductForm categories={categories} navigate={navigate} />
        )}

        {type === "blog" && <BlogForm navigate={navigate} />}
      </div>
    </div>
  );
};

/* ----------------------------------------------------------
   PRODUCT FORM
---------------------------------------------------------- */

const ProductForm = ({ categories, navigate }) => {
  const [title, setTitle] = useState("");
  const [category, setCategory] = useState("");
  const [price, setPrice] = useState("");
  const [description, setDescription] = useState("");
  const [amazonLink, setAmazonLink] = useState("");

  const [mainImage, setMainImage] = useState(null);
  const [hoverImage, setHoverImage] = useState(null);
  const [exampleImages, setExampleImages] = useState([]);

  const [selectedTags, setSelectedTags] = useState([]);
  const [loading, setLoading] = useState(false);

  const toggleTag = (tag) => {
    setSelectedTags((prev) =>
      prev.includes(tag)
        ? prev.filter((t) => t !== tag)
        : prev.length < 3
        ? [...prev, tag]
        : prev
    );
  };

  const uploadFile = async (file, path, compress = true) => {
    const finalFile = compress ? await compressImage(file) : file;
    const fileRef = ref(storage, `${path}/${Date.now()}-${file.name}`);
    await uploadBytes(fileRef, finalFile);
    return await getDownloadURL(fileRef);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const productRef = doc(collection(db, "products"));
      const id = productRef.id;

      const mainUrl = mainImage ? await uploadFile(mainImage, "products") : "";
      const hoverUrl = hoverImage ? await uploadFile(hoverImage, "products") : "";

      const exampleUrls = [];
      for (const img of exampleImages) {
        exampleUrls.push(await uploadFile(img, "examples"));
      }

      await setDoc(productRef, {
        title,
        category,
        price: parseFloat(price),
        description,
        amazonLink,
        image: mainUrl,
        image2: hoverUrl,
        examples: exampleUrls,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        likes: 0,
        views: 0,
        imageOptimized: true,
        tags: selectedTags,
      });

      navigate(`/${category}/${id}`);
    } catch (err) {
      console.error(err);
      alert("Upload failed.");
    }

    setLoading(false);
  };

  return (
    <form style={styles.form} onSubmit={handleSubmit}>
      <h2 style={styles.sectionTitle}>Product Upload</h2>

      <div style={styles.row}>
        <label style={styles.label}>Title</label>
        <input style={styles.input} value={title} onChange={(e) => setTitle(e.target.value)} />
      </div>

      <div style={styles.row}>
        <label style={styles.label}>Category</label>
        <select
          style={styles.input}
          value={category}
          onChange={(e) => setCategory(e.target.value)}
        >
          <option value="">Select Category</option>
          {categories.map((c) => (
            <option key={c.id} value={c.slug}>
              {c.name}
            </option>
          ))}
        </select>
      </div>

      <div style={styles.row}>
        <label style={styles.label}>Price</label>
        <input
          style={styles.input}
          type="number"
          value={price}
          onChange={(e) => setPrice(e.target.value)}
        />
      </div>

      <div style={styles.row}>
        <label style={styles.label}>Description</label>
        <textarea
          style={styles.textarea}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
        />
      </div>

      <div style={styles.row}>
        <label style={styles.label}>Amazon Link</label>
        <input
          style={styles.input}
          value={amazonLink}
          onChange={(e) => setAmazonLink(e.target.value)}
        />
      </div>

      <div style={styles.row}>
        <label style={styles.label}>Main Image</label>
        <input type="file" accept="image/*" onChange={(e) => setMainImage(e.target.files[0])} />
      </div>

      <div style={styles.row}>
        <label style={styles.label}>Hover Image</label>
        <input type="file" accept="image/*" onChange={(e) => setHoverImage(e.target.files[0])} />
      </div>

      <div style={styles.row}>
        <label style={styles.label}>Example Images</label>
        <input
          type="file"
          accept="image/*"
          multiple
          onChange={(e) => setExampleImages([...e.target.files])}
        />
      </div>

      <label style={styles.label}>Tags (max 3)</label>
      <div style={styles.tags}>
        {PRODUCT_TAGS.map((tag) => (
          <button
            type="button"
            key={tag}
            style={{
              ...styles.tag,
              background: selectedTags.includes(tag) ? "#111827" : "#f3f4f6",
              color: selectedTags.includes(tag) ? "#fff" : "#000",
            }}
            onClick={() => toggleTag(tag)}
          >
            {tag}
          </button>
        ))}
      </div>

      <button style={styles.button} disabled={loading}>
        {loading ? "Uploading..." : "Upload Product"}
      </button>
    </form>
  );
};

/* ----------------------------------------------------------
   BLOG FORM (ReactQuill + Category Removed)
---------------------------------------------------------- */

const BlogForm = ({ navigate }) => {
  const [title, setTitle] = useState("");
  const [body, setBody] = useState(""); // HTML from ReactQuill
  const [mediaFile, setMediaFile] = useState(null);
  const [selectedTags, setSelectedTags] = useState([]);
  const [loading, setLoading] = useState(false);
  const [embedUrl, setEmbedUrl] = useState("");
  const [embedThumbnail, setEmbedThumbnail] = useState("");
  const [summary, setSummary ] = useState("");
  
  const [idea, setIdea] = useState("");
  const [storyPrompt, setStoryPrompt] = useState("");
  const [aiLoading, setAiLoading] = useState(false);

  const [tone, setTone] = useState("neutral");
  const [genre, setGenre] = useState("Fantasy");

  const [autoTags, setAutoTags] = useState([]);



  const toggleTag = (tag) => {
    setSelectedTags((prev) =>
      prev.includes(tag)
        ? prev.filter((t) => t !== tag)
        : prev.length < 3
        ? [...prev, tag]
        : prev
    );
  };

  const uploadFile = async (file, path, compress = true) => {
    const finalFile =
      compress && file.type.startsWith("image/")
        ? await compressImage(file)
        : file;

    const fileRef = ref(storage, `${path}/${Date.now()}-${file.name}`);
    await uploadBytes(fileRef, finalFile);
    return await getDownloadURL(fileRef);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      let headerUrl = null;
      let videoUrl = null;
      let mediaTag = "Photo Post";

      if (mediaFile) {
        if (mediaFile.type.startsWith("image/")) {
          headerUrl = await uploadFile(mediaFile, "blogs/headers", true);
        } else {
          videoUrl = await uploadFile(mediaFile, "blogs/videos", false);
          mediaTag = "Video Post";
        }
      }

      const docRef = await addDoc(collection(db, "blogs"), {
        title,
        body, // HTML saved directly
        embedUrl,
        embedThumbnail,
        headerImage: headerUrl,
        video: videoUrl,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        likes: 0,
        views: 0,
        imageOptimized: true,
        tags: [...selectedTags, mediaTag],
        summary
      });

      logActivity("upload", docRef.id);
      navigate(`/blog/${docRef.id}`);
    } catch (err) {
      console.error(err);
      alert("Upload failed.");
    }

    setLoading(false);
  };

  const extractYouTubeId = (url) => {
    try {
      const reg =
        /(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([A-Za-z0-9_-]{11})/;
      const match = url.match(reg);
      return match ? match[1] : null;
    } catch {
      return null;
    }
  };

  const generatePrompt = async () => {
    if (!idea.trim()) return;
    setAiLoading(true);

    try {
      const response = await fetch("https://api.openai.com/v1/chat/completions", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${process.env.REACT_APP_OPENAI_KEY}`,
        },
        body: JSON.stringify({
          model: "gpt-4o-mini",
          messages: [
            {
              role: "system",
              content: "You generate creative story prompts based on user ideas.",
            },
            {
              role: "user",
              content: `Idea: ${idea}\nTone: ${tone}\nGenre: ${genre}\n\nCreate a compelling story prompt.`,
            },
          ],
        }),
      });

      const data = await response.json();
      setStoryPrompt(data.choices[0].message.content.trim());
    } catch (err) {
      console.error(err);
      alert("Failed to generate prompt.");
    }

    setAiLoading(false);
  };

  const generateStoryFromPrompt = async () => {
    if (!storyPrompt.trim()) return;
    setAiLoading(true);

    try {
      const response = await fetch("https://api.openai.com/v1/chat/completions", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${process.env.REACT_APP_OPENAI_KEY}`,
        },
        body: JSON.stringify({
          model: "gpt-4o-mini",
          messages: [
            {
              role: "system",
              content:
                "You write full blog stories with a title, summary, and HTML body.",
            },
            {
              role: "user",
              content: `Write a full blog story based on this prompt:\n\n${storyPrompt}\nTone: ${tone}\nGenre: ${genre}\n\nReturn JSON with keys: title, summary, body, tags.`,
            },
          ],
        }),
      });

      const data = await response.json();
      const json = JSON.parse(data.choices[0].message.content);

      setTitle(json.title);
      setSummary(json.summary);
      setBody(json.body);
      setAutoTags(json.tags || []);
    } catch (err) {
      console.error(err);
      alert("Failed to generate story.");
    }

    setAiLoading(false);
  };

  return (
    <form style={styles.form} onSubmit={handleSubmit}>
      <h2 style={styles.sectionTitle}>Blog Upload</h2>

     <h3 style={{ marginTop: 30 }}>AI Story Generator</h3>

    {/* TONE SELECTOR */}
    <div style={styles.row}>
      <label style={styles.label}>Tone</label>
      <select
        style={styles.input}
        value={tone}
        onChange={(e) => setTone(e.target.value)}
      >
        <option value="neutral">Neutral</option>
        <option value="funny">Funny</option>
        <option value="dark">Dark</option>
        <option value="emotional">Emotional</option>
        <option value="epic">Epic</option>
        <option value="wholesome">Wholesome</option>
      </select>
    </div>

    {/* GENRE SELECTOR */}
    <div style={styles.row}>
      <label style={styles.label}>Genre</label>
      <select
        style={styles.input}
        value={genre}
        onChange={(e) => setGenre(e.target.value)}
      >
        {BLOG_GENRE_TAGS.map((tag) => (
          <option key={tag} value={tag}>{tag}</option>
        ))}
      </select>
    </div>

    {/* IDEA INPUT */}
    <div style={styles.row}>
      <label style={styles.label}>Your Idea</label>
      <input
        style={styles.input}
        value={idea}
        onChange={(e) => setIdea(e.target.value)}
        placeholder="A girl finds a glowing stone in the forest..."
      />
    </div>

    <button
      type="button"
      style={styles.button}
      onClick={generatePrompt}
      disabled={aiLoading}
    >
      {aiLoading ? "Generating..." : "Generate Story Prompt"}
    </button>

    {/* STORY PROMPT */}
    {storyPrompt && (
      <>
        <div style={styles.row}>
          <label style={styles.label}>Story Prompt</label>
          <textarea
            style={{ ...styles.input, height: 120 }}
            value={storyPrompt}
            onChange={(e) => setStoryPrompt(e.target.value)}
          />
        </div>

        <div style={{ display: "flex", gap: 10 }}>
          <button
            type="button"
            style={styles.button}
            onClick={generateStoryFromPrompt}
            disabled={aiLoading}
          >
            {aiLoading ? "Generating Story..." : "Generate Full Story"}
          </button>

          <button
            type="button"
            style={{ ...styles.button, background: "#6b7280" }}
            onClick={generatePrompt}
            disabled={aiLoading}
          >
            Regenerate Prompt
          </button>

          <button
            type="button"
            style={{ ...styles.button, background: "#6b7280" }}
            onClick={generateStoryFromPrompt}
            disabled={aiLoading}
          >
            Regenerate Story
          </button>
        </div>
      </>
    )}


      {/* TITLE */}
      <div style={styles.row}>
        <label style={styles.label}>Title</label>
        <input
          style={styles.input}
          value={title}
          onChange={(e) => setTitle(e.target.value)}
        />
      </div>

      {/* BODY (ReactQuill) */}
      <div style={styles.row}>
        <label style={styles.label}>Body</label>
        <div style={{ flex: 1 }}>
          <ReactQuill
            theme="snow"
            value={body}
            onChange={setBody}
            style={{
              background: "#fff",
              borderRadius: "8px",
            }}
          />
        </div>
      </div>

      <div style={styles.row}>
        <label style={styles.label}>Summary</label>
          <input
            value={summary}
            onChange={(e) => setSummary(e.target.value)}
            style={styles.input}
          />
      </div>

      <div style={styles.row}>
        <label style={styles.label}>YouTube Embed URL</label>
        <input
          style={styles.input}
          placeholder="https://www.youtube.com/embed/VIDEO_ID"
          value={embedUrl}
          onChange={(e) => {
            const url = e.target.value;
            setEmbedUrl(url);

            const id = extractYouTubeId(url);
            if (id) {
              setEmbedThumbnail(`https://img.youtube.com/vi/${id}/hqdefault.jpg`);
            }
          }}
        />
      </div>

      {/* MEDIA */}
      <div style={styles.row}>
        <label style={styles.label}>Media</label>
        <input
          type="file"
          accept="image/*,video/*"
          style={styles.input}
          onChange={(e) => {
            const file = e.target.files[0];
            if (!file) return;
            setMediaFile(file);
          }}
        />
      </div>

      {/* TAGS */}
      <div style={styles.row}>
        <label style={styles.label}>Tags</label>
        <div style={styles.tags}>
          {BLOG_GENRE_TAGS.map((tag) => (
            <button
              type="button"
              key={tag}
              style={{
                ...styles.tag,
                background: selectedTags.includes(tag) ? "#111827" : "#f3f4f6",
                color: selectedTags.includes(tag) ? "#fff" : "#000",
              }}
              onClick={() => toggleTag(tag)}
            >
              {tag}
            </button>
          ))}
        </div>
      </div>

      {autoTags.length > 0 && (
        <div style={{ marginTop: 10 }}>
          <strong>Suggested Tags:</strong>
          <div style={styles.tags}>
            {autoTags.map((tag) => (
              <button
                type="button"
                key={tag}
                style={{
                  ...styles.tag,
                  background: selectedTags.includes(tag) ? "#111827" : "#e5e7eb",
                  color: selectedTags.includes(tag) ? "#fff" : "#000",
                }}
                onClick={() => toggleTag(tag)}
              >
                {tag}
              </button>
            ))}
          </div>
        </div>
      )}

      <button style={styles.button} disabled={loading}>
        {loading ? "Uploading..." : "Upload Blog Post"}
      </button>
    </form>
  );
};



/* ----------------------------------------------------------
   STYLES — Modern CMS with Left-Aligned Labels
---------------------------------------------------------- */

const styles = {
  container: {
    maxWidth: "820px",
    margin: "50px auto",
    padding: "40px",
    background: "#ffffff",
    borderRadius: "14px",
    boxShadow: "0 8px 30px rgba(0,0,0,0.08)",
    border: "1px solid #e5e7eb",
  },

  title: {
    fontSize: "32px",
    marginBottom: "25px",
    fontWeight: "700",
    textAlign: "center",
    color: "#111827",
  },

  sectionTitle: {
    fontSize: "24px",
    marginBottom: "10px",
    fontWeight: "600",
    color: "#111827",
  },

  form: {
    display: "flex",
    flexDirection: "column",
    gap: "22px",
    marginTop: "25px",
  },

  row: {
    display: "flex",
    alignItems: "center",
    gap: "20px",
  },

  label: {
    width: "20%",
    fontSize: "16px",
    fontWeight: "600",
    color: "#374151",
    textAlign: "right"
  },

  input: {
    width: "80%",
    padding: "14px 16px",
    fontSize: "16px",
    borderRadius: "10px",
    border: "1px solid #d1d5db",
    background: "#f9fafb",
    transition: "all 0.2s ease",
  },

  textarea: {
    width: "80%",
    padding: "14px 16px",
    fontSize: "16px",
    borderRadius: "10px",
    border: "1px solid #d1d5db",
    background: "#f9fafb",
    minHeight: "140px",
    transition: "all 0.2s ease",
  },

  tags: {
    display: "flex",
    flexWrap: "wrap",
    gap: "10px",
    marginLeft: "10%",
  },

  tag: {
    padding: "8px 14px",
    borderRadius: "20px",
    cursor: "pointer",
    border: "1px solid #d1d5db",
    background: "#f3f4f6",
    fontSize: "14px",
    transition: "all 0.2s ease",
  },

  button: {
    padding: "16px",
    fontSize: "18px",
    borderRadius: "10px",
    background: "#111827",
    color: "#fff",
    cursor: "pointer",
    border: "none",
    marginTop: "10px",
    transition: "all 0.2s ease",
  },
};

export default AdminUpload;
